import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import ArticleCard from '@/components/feature/ArticleCard';
import { articles, categories } from '@/mocks/skincare';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);
  const categoryName = article ? categories.find((c) => c.slug === article.category)?.name || article.category : '';
  const relatedArticles = article
    ? articles.filter((a) => a.category === article.category && a.slug !== article.slug).slice(0, 3)
    : [];

  if (!article) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="min-h-screen flex items-center justify-center px-6">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-4">
              <i className="ri-article-line text-2xl"></i>
            </div>
            <h2 className="text-xl font-semibold text-foreground-950 mb-2">文章未找到</h2>
            <p className="text-sm text-foreground-500 mb-6">该文章不存在或已被移除</p>
            <Link to="/" className="px-5 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm hover:bg-primary-600 transition-colors whitespace-nowrap">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const breadcrumbItems = [
    { label: '首页', to: '/' },
    { label: categoryName, to: `/category/${article.category}` },
    { label: article.title },
  ];

  const pageUrl = `https://jjchuang.cn/article/${slug}`;

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main>
        <article>
          <div className="bg-background-100/70 border-b border-background-200/70">
            <div className="max-w-4xl mx-auto px-6 md:px-10 py-8">
              <Breadcrumb items={breadcrumbItems} />
              <div className="mt-4">
                <Link
                  to={`/category/${article.category}`}
                  className="inline-block text-xs px-2.5 py-1 rounded-full bg-accent-100 text-accent-700 hover:bg-accent-200 transition-colors mb-4 whitespace-nowrap"
                >
                  {categoryName}
                </Link>
                <h1 className="text-2xl md:text-4xl font-semibold text-foreground-950 leading-tight mb-4" style={{ fontFamily: "var(--font-heading)" }}>
                  {article.title}
                </h1>
                <div className="flex items-center gap-4 text-sm text-foreground-500 flex-wrap">
                  <span className="flex items-center gap-1 whitespace-nowrap">
                    <i className="ri-user-line"></i>
                    {article.author}
                  </span>
                  <span className="flex items-center gap-1 whitespace-nowrap">
                    <i className="ri-calendar-line"></i>
                    {article.date}
                  </span>
                  <span className="flex items-center gap-1 whitespace-nowrap">
                    <i className="ri-time-line"></i>
                    {article.readTime}
                  </span>
                </div>
                <div className="flex items-center gap-2 mt-4 flex-wrap">
                  {article.tags.map((tag) => (
                    <span key={tag} className="text-xs px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-700 whitespace-nowrap">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="max-w-4xl mx-auto px-6 md:px-10 py-10">
            <div className="rounded-lg overflow-hidden mb-10 aspect-[16/9]">
              <img
                src={article.image}
                alt={article.title}
                className="w-full h-full object-cover"
              />
            </div>

            <div
              className="prose prose-sm md:prose-base max-w-none text-foreground-800 leading-relaxed article-content"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="mt-12 pt-8 border-t border-background-200/70 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-secondary-100 flex items-center justify-center text-secondary-600 text-sm font-medium">
                  {article.author.charAt(0)}
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground-950">{article.author}</p>
                  <p className="text-xs text-foreground-500">裸肌研究所 资深编辑</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-xs text-foreground-500 whitespace-nowrap">分享到：</span>
                <a href="#" className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-500 transition-colors" aria-label="分享到微信">
                  <i className="ri-wechat-line"></i>
                </a>
                <a href="#" className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-500 transition-colors" aria-label="分享到微博">
                  <i className="ri-weibo-line"></i>
                </a>
                <a href="#" className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-500 transition-colors" aria-label="复制链接">
                  <i className="ri-link"></i>
                </a>
              </div>
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="py-12 md:py-16 px-6 md:px-10 bg-background-100/70">
            <div className="max-w-7xl mx-auto">
              <h2 className="text-xl md:text-2xl font-semibold text-foreground-950 mb-8" style={{ fontFamily: "var(--font-heading)" }}>
                相关文章
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {relatedArticles.map((ra) => (
                  <ArticleCard key={ra.slug} {...ra} />
                ))}
              </div>
            </div>
          </section>
        )}

        <section className="py-12 px-6 md:px-10">
          <div className="max-w-4xl mx-auto text-center">
            <Link
              to={`/category/${article.category}`}
              className="inline-flex items-center gap-2 px-6 py-3 border border-primary-300 text-primary-600 rounded-full text-sm hover:bg-primary-50 transition-colors whitespace-nowrap"
            >
              <i className="ri-arrow-left-line"></i>
              返回 {categoryName}
            </Link>
          </div>
        </section>
      </main>
      <Footer />

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Article",
        "headline": article.title,
        "description": article.excerpt,
        "author": {
          "@type": "Person",
          "name": article.author
        },
        "datePublished": article.date,
        "dateModified": article.date,
        "url": pageUrl,
        "inLanguage": "zh-CN",
        "isPartOf": { "@type": "WebSite", "name": "裸肌研究所", "url": "https://jjchuang.cn" }
      }) }}></script>

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "首页", "item": "https://jjchuang.cn" },
          { "@type": "ListItem", "position": 2, "name": categoryName, "item": `https://jjchuang.cn/category/${article.category}` },
          { "@type": "ListItem", "position": 3, "name": article.title, "item": pageUrl }
        ]
      }) }}></script>
    </>
  );
}